import React from "react";
import moment from "moment/moment";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { DateCalendar } from "@mui/x-date-pickers/DateCalendar";
import Box from "@mui/material/Box";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import OutlinedInput from "@mui/material/OutlinedInput";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import { usePopupState, bindPopover, bindTrigger } from "material-ui-popup-state/hooks";
import Popover from "@mui/material/Popover";
import EventIcon from "@mui/icons-material/Event";
import FormHelperText from "@mui/material/FormHelperText";
import LinearProgress from "@mui/material/LinearProgress";
import useBlackoutDates from "../../../hooks/shopping/useBlackoutDates";
import {
  getRestrictedDays,
  isDateRestricted,
} from "../../../views/inventory/requests/orders/utils";
import TimePicker from "./TimePicker";
import useRoles from "../../../hooks/user/useRoles";

const dateFormat = "yyyy-MM-DDTHH:mm:ss";

const styles = {
  popover: {
    mt: 1,
  },
  disabledDay: {
    "& .MuiPickersDay-root.Mui-disabled": {
      color: "red",
      textDecoration: "line-through",
    },
  },
};

const getDateTime = (value) => {
  const dateTime = moment(value).format(dateFormat);
  const [date, time] = dateTime?.split("T") || [];
  return { dateTime, date, time };
};

export default function DateTimePicker({
  label,
  defaultValue,
  minDate,
  error,
  helperText,
  onChange,
  disabled = false,
}) {
  const { loading, blackoutDates, refetch } = useBlackoutDates();
  const isAdminOrManager = useRoles(["admin", "manager"]);
  const restrictedDays = isAdminOrManager ? [] : getRestrictedDays(blackoutDates);
  const [value, setValue] = React.useState(defaultValue ? moment(defaultValue) : "");
  const popupState = usePopupState({ variant: "popover", popupId: "demoPopover" });
  const inputProps = bindTrigger(popupState);
  const isRestrictedDate =
    !isAdminOrManager &&
    value &&
    isDateRestricted(blackoutDates, moment(value).format("YYYY-MM-DD"));

  const handleChange = (dateValue) => {
    dateValue.set("second", 0);
    setValue(dateValue);
    const { dateTime, date, time } = getDateTime(dateValue);
    onChange({ dateTime, date, time });
  };

  const handleChangeDay = (newValue) => {
    const newDateTime = value ? newValue : newValue.set("hour", 8);
    if (minDate) {
      if (minDate > newValue) {
        newValue.set("hour", moment(minDate).get("hour") + 1);
        newValue.set("minute", 0);
      }
    }
    handleChange(newDateTime);
  };

  const shouldDisableDate = (date) => {
    const formattedDate = date.format("YYYY-MM-DD");
    return restrictedDays.some((day) => day.date === formattedDate);
  };

  const handleClick = (...data) => {
    if (disabled) return;
    refetch();
    return inputProps.onClick(...data);
  };

  const hasError = error || isRestrictedDate;
  const errorMessage = isRestrictedDate
    ? "Selected date has been marked as unavailable"
    : helperText;

  return (
    <LocalizationProvider dateAdapter={AdapterMoment}>
      <div>
        <FormControl fullWidth variant="outlined">
          <InputLabel shrink>{label}</InputLabel>
          <OutlinedInput
            {...inputProps}
            onClick={handleClick}
            error={hasError}
            helperText={errorMessage}
            value={value ? value.format("MM/DD/YYYY hh:mm A") : " "}
            label={label}
            fullWidth
            size="small"
            autoComplete="off"
            disabled={disabled}
            endAdornment={
              <InputAdornment position="end">
                <IconButton>
                  <EventIcon />
                </IconButton>
              </InputAdornment>
            }
          />
          {hasError && <FormHelperText error>{errorMessage}</FormHelperText>}
        </FormControl>
        <Popover
          {...bindPopover(popupState)}
          disableAutoFocus
          sx={styles.popover}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
        >
          {loading && <LinearProgress />}
          <Box display="flex">
            <Box sx={styles.disabledDay}>
              <DateCalendar
                minDate={minDate}
                value={value || null}
                onChange={handleChangeDay}
                shouldDisableDate={shouldDisableDate}
              />
            </Box>
            <TimePicker minDate={minDate} value={value} onChange={handleChange} />
          </Box>
        </Popover>
      </div>
    </LocalizationProvider>
  );
}
